import { useEffect } from 'react';

import type { IMultiStepActions } from '@farmshare/ui-components';

import { useCustomerMergePreviewQuery } from '../../../lib/graphql';

import {
  isSelectionConsistent,
  type MergeWizardData,
  requiresConsent,
  selectedIdsOf,
} from '../hooks/merge-wizard-data';
import {
  type MergeReviewPreview,
  MergeWizardReviewStep,
} from './merge-wizard-review-step';

export interface MergeWizardReviewModalStepProps {
  data: MergeWizardData;
  actions: IMultiStepActions<MergeWizardData>;
  /** Vendor the merge runs against. */
  vendorId: string | undefined;
}

/**
 * Step 3 of the merge wizard — confirm scope and consent, with the server's
 * merge preview.
 * @param root0 - Props.
 * @param root0.data - Current wizard data.
 * @param root0.actions - Multi-step actions.
 * @param root0.vendorId - Vendor the merge runs against.
 * @returns The step body.
 */
export function MergeWizardReviewModalStep({
  data,
  actions,
  vendorId,
}: MergeWizardReviewModalStepProps) {
  const { updateData } = actions;
  const selectedIds = selectedIdsOf(data);
  const loserIds = selectedIds.filter((id) => id !== data.masterId);

  const {
    data: previewData,
    loading,
    error,
  } = useCustomerMergePreviewQuery({
    variables: { winnerId: data.masterId, loserIds, vendorId: vendorId! },
    // Never preview a merge into a record outside the selection — the gate
    // refuses it too, and without a preview Merge stays disabled.
    skip: !isSelectionConsistent(data) || !vendorId || !loserIds.length,
    fetchPolicy: 'network-only',
  });

  const preview = (previewData?.customerMergePreview ??
    null) as MergeReviewPreview | null;

  useEffect(() => {
    updateData({ preview, previewLoading: loading });
  }, [preview, loading, updateData]);

  return (
    <MergeWizardReviewStep
      candidates={data.candidates}
      masterId={data.masterId}
      fieldSources={data.fieldSources}
      preview={preview}
      previewLoading={loading}
      // A failed preview leaves `preview` null and Merge disabled; without
      // surfacing the error the user gets no explanation for the dead button.
      errorMessage={error?.message ?? null}
      requireConsent={requiresConsent(data)}
      consent={data.consent}
      onConsentChange={(value) => updateData({ consent: value })}
    />
  );
}
